import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  BiContextProvidedValue,
  BiData,
  AddWidgetBiEvent,
  MoveWidgetClickBiEvent,
  RemoveWidgetClickBiEvent,
} from '@wix/da-bi/pkg/events';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/ArrowDown';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/ArrowLeft';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/ArrowRight';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import EditConfigButton from './EditConfigButton';
import AddColumnButton from './AddColumnButton';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/Preset/TextTooltipOnHover';

import s from './WidgetEditControls.scss';

export interface Props {
  className?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  onEdit?: () => void;
  onAddColumn?: () => void;
  tooltipTitle?: string;
  tooltipContent?: React.ReactNode;
  direction?: 'row' | 'column';
}

const WidgetEditControls: React.FC<Props> = ({
  tooltipTitle,
  tooltipContent,
  onEdit,
  onMoveUp,
  onRemove,
  className,
  onMoveDown,
  onAddColumn,
  direction = 'column',
}) => {
  const { t } = useTranslation();
  if (!onEdit && !onMoveDown && !onMoveUp && !onRemove) {
    return null;
  }

  const renderEditConfigButton = () => {
    if (tooltipContent) {
      return (
        <TextTooltipOnHover
          placement="top"
          title={tooltipTitle}
          tooltipText={tooltipContent}
          renderReference={({ ref }) => (
            <EditConfigButton onClick={onEdit} iconRef={ref} />
          )}
        />
      );
    }

    return <EditConfigButton onClick={onEdit} />;
  };

  return (
    <div className={classnames(s['root'], className)}>
      {direction === 'column' && onMoveUp && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveUp')}
          onClick={onMoveUp}
          className={s['icon-button']}
          data-role="edit-control"
          biData={BiData<MoveWidgetClickBiEvent>({
            evid: 281,
            direction: 'up',
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
          aria-label={t('widgets.common.moveUp')}
        >
          <ArrowUpIcon size={IconSize.MEDIUM} className={s['icon']} condensed />
        </IconButton>
      )}
      {direction === 'column' && onMoveDown && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveDown')}
          onClick={onMoveDown}
          className={s['icon-button']}
          data-role="edit-control"
          biData={BiData<MoveWidgetClickBiEvent>({
            evid: 281,
            direction: 'down',
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
          aria-label={t('widgets.common.moveDown')}
        >
          <ArrowDownIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {direction === 'row' && onMoveUp && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveLeft')}
          onClick={onMoveUp}
          className={s['icon-button']}
          data-role="edit-control"
          biData={BiData<MoveWidgetClickBiEvent>({
            evid: 281,
            direction: 'left',
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
          aria-label={t('widgets.common.moveLeft')}
        >
          <ArrowLeftIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {direction === 'row' && onMoveDown && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveRight')}
          onClick={onMoveDown}
          className={s['icon-button']}
          data-role="edit-control"
          biData={BiData<MoveWidgetClickBiEvent>({
            evid: 281,
            direction: 'right',
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
          aria-label={t('widgets.common.moveRight')}
        >
          <ArrowRightIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {onRemove && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.delete')}
          onClick={onRemove}
          className={s['icon-button']}
          data-role="edit-control"
          biData={BiData<RemoveWidgetClickBiEvent>({
            evid: 280,
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
          aria-label={t('widgets.common.delete')}
        >
          <TrashcanIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {onEdit && renderEditConfigButton()}
      {onAddColumn && (
        <AddColumnButton
          onClick={onAddColumn}
          biData={BiData<AddWidgetBiEvent>({
            evid: 283,
            widgetname: BiContextProvidedValue,
            widgetzone: BiContextProvidedValue,
          })}
        />
      )}
    </div>
  );
};

WidgetEditControls.displayName = 'WidgetEditControls';

export default WidgetEditControls;
